/**
 * Studio HelperPanel — Phase 4B (#147).
 *
 * Embedded chat panel for the three sysadmin-published Helper agents
 * (tool / skill / prompt). Renders alongside the create flow at
 *   /studio/tools/new      (kind = tool_helper)
 *   /studio/skills (sheet) (kind = skill_helper)
 *   /studio/prompts (sheet)(kind = prompt_helper)
 *
 * The helper agent itself is loaded from system_agents (resolved by
 * `kind` in the GET /studio/helper/agents endpoint). When no helper
 * is published, the panel renders an honest "no helper available"
 * placeholder rather than a broken chat input.
 *
 * Q5: the helper sees STRUCTURAL catalog metadata only (server-side
 * snapshot — names + types + counts; never source code or auth
 * secrets). Helpers cannot call tools and cannot fetch tenant data.
 */

import { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, RotateCcw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { useHelperAgent, useHelperChat, type HelperKind, type HelperScope, type PendingMutation } from '@/lib/use-studio-helper';
import { AcceptRejectCard } from '@/components/canvas/accept-reject-card';
import { cn } from '@/lib/utils';

interface HelperPanelProps {
  kind: HelperKind;
  /**
   * Day-1: 'tenant' (default — back-compat) or 'sysadmin'.
   */
  scope?: HelperScope;
  /**
   * Day-1: current draft on the canvas. The helper sees a truncated
   * JSON dump of this in the system prompt so it can patch what exists
   * rather than restart.
   */
  canvasState?: Record<string, unknown>;
  /**
   * Day-5: caller-supplied handler for mutation tool calls. Receives
   * each pending mutation when the user clicks Accept; should call
   * back into the caller's form-state setters. When undefined, the
   * helper falls back to chat-only mode (mutation cards are shown
   * but Accept is a no-op — useful for surfaces that haven't yet
   * wired the canvas).
   */
  onApplyMutation?: (m: PendingMutation) => void;
  /**
   * Optional intro shown above the chat once the user opens the panel.
   * The Helper's system prompt does most of the heavy lifting; this is
   * just framing for the user.
   */
  intro?: string;
  className?: string;
}

const TITLE_BY_KIND: Record<HelperKind, string> = {
  tool_helper: 'Tool Helper',
  skill_helper: 'Skill Helper',
  prompt_helper: 'Prompt Helper',
  architect: 'Argus Architect',
  function_helper: 'Function Helper',
  // AL-B3 renders its own 3-pane editor for argus_live_author; this entry
  // satisfies the HelperKind record exhaustiveness check.
  argus_live_author: 'Argus Live Author',
  // AL-C1 — Argus Live publish-time safety reviewer. Production
  // invocation goes through the publish flow (`architect-review.service.ts`),
  // not the helper-panel. Entry satisfies the HelperKind record
  // exhaustiveness check; if the panel is ever rendered with this kind
  // (debug / preview surface) the title shows up as "Argus Live Architect".
  argus_live_architect: 'Argus Live Architect',
};

const PLACEHOLDER_BY_KIND: Record<HelperKind, string> = {
  tool_helper:
    'e.g. "Help me wire up an HTTP tool that fetches our daily inventory snapshot from the warehouse API."',
  skill_helper:
    'e.g. "I want a skill that walks an agent through a shift handover for the maintenance team."',
  prompt_helper:
    'e.g. "Draft a /summarize-incident slash command that takes an alarm id + timeframe and produces an incident summary."',
  architect:
    'e.g. "Build me a maintenance agent for pumps that reads vibration telemetry and suggests work orders for approval."',
  function_helper:
    'e.g. "I need a function that calls the Stripe API to fetch a customer\'s recent charges."',
  argus_live_author:
    'e.g. "Build me a kanban board that pulls open GitHub issues for the platform repo."',
  // AL-C1 — production invocation does not pass through the chat panel.
  // This placeholder appears only on the debug surface where an admin
  // hand-feeds a draft to the reviewer to preview its verdict.
  argus_live_architect:
    'Paste the dashboard draft (code_html, code_js, code_css, data_bindings) as JSON and the reviewer will return a verdict.',
};

export function HelperPanel({ kind, intro, scope, canvasState, onApplyMutation, className }: HelperPanelProps) {
  const { agent, isLoading: loadingAgent } = useHelperAgent(kind, scope);
  const chat = useHelperChat({ kind, scope, canvasState });
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const applyMutation = (m: PendingMutation) => {
    if (onApplyMutation) onApplyMutation(m);
    chat.consumeMutation(m.callId);
  };
  const applyAll = () => {
    for (const m of chat.pendingMutations) {
      if (m.confirmed && m.turnComplete && !m.invalidReason) applyMutation(m);
    }
  };

  // Auto-scroll on new tokens.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [chat.messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || chat.isStreaming) return;
    setInput('');
    await chat.send(text);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Card className={cn('flex flex-col h-full', className)}>
      <div className="border-b px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-violet-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">
              {TITLE_BY_KIND[kind]}
            </div>
            {agent ? (
              <div className="text-[11px] text-muted-foreground truncate">
                {agent.displayName} · v{agent.version}
              </div>
            ) : (
              <div className="text-[11px] text-muted-foreground">Loading…</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {chat.messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={chat.reset}
              disabled={chat.isStreaming}
              className="h-7 w-7 p-0"
              title="Reset conversation"
              aria-label="Reset conversation"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {!loadingAgent && !agent ? (
        <CardContent className="py-4 text-xs text-muted-foreground space-y-2">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
            <div>
              <p className="font-medium text-foreground">No {TITLE_BY_KIND[kind]} published</p>
              <p className="mt-1">
                Sysadmin must seed or author a system agent of kind{' '}
                <code className="bg-muted px-1 rounded">{kind}</code> in{' '}
                <code className="bg-muted px-1 rounded">/sysadmin/studio/agents</code>{' '}
                before this panel will work. The form fields on the right are still usable manually.
              </p>
            </div>
          </div>
        </CardContent>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {chat.messages.length === 0 && (
              <div className="text-xs text-muted-foreground space-y-2">
                {intro && <p>{intro}</p>}
                {agent && <p className="italic">{agent.description}</p>}
                <p>
                  <strong>What I can see:</strong> the structural metadata of your tenant catalog
                  (tools, skills, prompts, agents, connection counts) — names and types only.
                </p>
                <p>
                  <strong>What I can't do:</strong> call any tool, read documents, or fetch live tenant data.
                </p>
              </div>
            )}
            {chat.messages.map((m, i) => (
              <div
                key={i}
                className={cn(
                  'rounded-md p-2 text-sm',
                  m.role === 'user'
                    ? 'bg-primary/5 ml-6 border border-primary/20'
                    : 'bg-muted/30 mr-6',
                )}
              >
                <Badge variant="outline" className="text-[9px] mb-1.5">
                  {m.role === 'user' ? 'You' : agent?.displayName ?? 'Helper'}
                </Badge>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <MarkdownRenderer content={m.content || (chat.isStreaming && i === chat.messages.length - 1 ? '_thinking…_' : '')} />
                </div>
              </div>
            ))}
            {chat.error && (
              <div className="text-[11px] text-destructive border border-destructive/30 rounded p-2">
                {chat.error}
              </div>
            )}
          </div>

          {/* Day 5 §3.3 — Accept/Reject card stack above the input.
              Surfaces only when the helper has emitted mutation tool
              calls AND the caller wired an onApplyMutation handler.
              When there's no handler we still surface the cards as a
              read-only preview so the user sees what the helper is
              proposing — Accept becomes a no-op (caller wires later). */}
          {chat.pendingMutations.length > 0 && (
            <div className="px-2 pt-2">
              <AcceptRejectCard
                mutations={chat.pendingMutations}
                helperLabel={agent?.displayName ?? TITLE_BY_KIND[kind]}
                applyMutation={applyMutation}
                rejectMutation={(m) => chat.consumeMutation(m.callId)}
                applyAll={applyAll}
                rejectAll={() => chat.clearMutations()}
              />
            </div>
          )}

          <div className="border-t p-2">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder={PLACEHOLDER_BY_KIND[kind]}
                rows={2}
                className="text-xs resize-none"
                disabled={chat.isStreaming || !agent || chat.pendingMutations.length > 0}
              />
              <Button
                size="sm"
                onClick={handleSend}
                disabled={!input.trim() || chat.isStreaming || !agent || chat.pendingMutations.length > 0}
                className="gap-1 shrink-0"
                title={chat.pendingMutations.length > 0 ? 'Resolve pending suggestions first' : 'Send'}
              >
                <Send className="h-3.5 w-3.5" />
                {chat.isStreaming ? '…' : 'Send'}
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
